import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import Icon from '../components/Icon.jsx'
import Modal from '../components/Modal.jsx'
import GlyphTile from '../components/GlyphTile.jsx'
import LookPicker from '../components/LookPicker.jsx'
import { lucideGlyph } from '../components/LucideIcon.jsx'
import { CardHead, Switch } from '../components/SettingsLayout.jsx'
import { useToast } from '../components/Toast.jsx'
import { DEFAULT_LIST_COLOR, DEFAULT_LIST_ICON, listPath } from '../lib/lists.js'

/**
 * Settings → Lists: every list the company keeps, and — for an owner — the means to add,
 * rename, recolour, reorder and retire them.
 *
 * Read by everyone the moment lists are on, the way the asset register is: the page is the
 * answer to "what lists do we keep", and a list nobody can find is a list nobody fills in.
 * The server says in the payload whether this person may change anything (`canManage`),
 * and the page reads that one flag rather than re-deriving the rule from roles. Hiding a
 * button is a courtesy, never the control — every write is refused server-side too.
 *
 * Retired lists stay on the page, dimmed, so they can be brought back. Nothing here deletes.
 */
export function ListsSettings() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data } = useQuery({
    queryKey: ['lists', 'all'],
    queryFn: () => api.get('/api/lists?includeInactive=1'),
  })
  const [editing, setEditing] = useState(null) // null | 'new' | a list

  // The boards as well as the lists: a board draws its list's name, colour and what it shows
  // beside each name, and this tab's own save is not echoed back to it over the live channel
  // (useRealtime ignores a tab's own writes), so without this a board opened straight after
  // saving could go on showing the old chips until its next refetch.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['lists'] })
    qc.invalidateQueries({ queryKey: ['list-board'] })
  }
  const lists = data?.lists || []
  const canManage = Boolean(data?.canManage)

  async function patch(list, body, said) {
    try {
      await api.patch(`/api/lists/${list.id}`, body)
      refresh()
      if (said) toast.success(said)
    } catch (err) {
      toast.error(err)
    }
  }

  // Up and down, one place at a time. Retired lists keep their place in the order so
  // bringing one back puts it where it was.
  async function move(list, dir) {
    const ids = lists.map((l) => l.id)
    const i = ids.indexOf(list.id)
    const j = i + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    try {
      await api.put('/api/lists/reorder', { ids })
      refresh()
    } catch (err) {
      toast.error(err)
    }
  }

  return (
    <div className="card">
      <CardHead
        title="Lists"
        sub="Create and Manage extra Scheduling Boards"
        action={
          canManage ? (
            <button className="btn primary" onClick={() => setEditing('new')}>
              <Icon name="plus" size={16} /> Add list
            </button>
          ) : null
        }
      />

      {!data ? (
        <div className="empty">Loading…</div>
      ) : lists.length === 0 ? (
        <div className="empty">No lists yet.{canManage ? ' Add one above.' : ''}</div>
      ) : (
        <div className="list-rows">
          {lists.map((l, i) => (
            <div key={l.id} className={`list-row ${l.active ? '' : 'retired'}`}>
              <GlyphTile icon={lucideGlyph(l.icon)} tint={l.color} size={32} muted={!l.active} />
              <div className="list-row-text">
                <div className="list-row-name">
                  {l.name}
                  {!l.active && <span className="tag muted">Retired</span>}
                </div>
                {l.description && <div className="sub">{l.description}</div>}
              </div>
              <Link className="btn sm" to={listPath(l)}>Open</Link>
              {canManage && (
                <>
                  <button
                    type="button"
                    className="icon-btn"
                    title="Move up"
                    aria-label={`Move ${l.name} up`}
                    disabled={i === 0}
                    onClick={() => move(l, -1)}
                  >
                    <Icon name="up" size={15} />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    title="Move down"
                    aria-label={`Move ${l.name} down`}
                    disabled={i === lists.length - 1}
                    onClick={() => move(l, 1)}
                  >
                    <Icon name="down" size={15} />
                  </button>
                  <button type="button" className="btn sm" onClick={() => setEditing(l)}>
                    <Icon name="edit" size={13} /> Edit
                  </button>
                  <button
                    type="button"
                    className="btn sm ghost"
                    onClick={() =>
                      patch(l, { active: !l.active }, l.active ? `${l.name} retired.` : `${l.name} is back.`)
                    }
                  >
                    {l.active ? 'Retire' : 'Restore'}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ListModal
          list={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh() }}
        />
      )}
    </div>
  )
}

/**
 * One form for a new list and for an existing one: name, description, colour and glyph, and
 * what the list shows beside each person on it.
 */
function ListModal({ list, onClose, onSaved }) {
  const toast = useToast()
  const [name, setName] = useState(list?.name || '')
  const [description, setDescription] = useState(list?.description || '')
  const [color, setColor] = useState(list?.color || DEFAULT_LIST_COLOR)
  const [icon, setIcon] = useState(list?.icon || DEFAULT_LIST_ICON)
  // Off for a new list, as the server has it (migrations/023).
  const [showPhone, setShowPhone] = useState(Boolean(list?.showPhone))
  const [showTitle, setShowTitle] = useState(Boolean(list?.showTitle))
  const [busy, setBusy] = useState(false)

  async function save(e) {
    e.preventDefault()
    setBusy(true)
    try {
      const body = { name, description, color, icon, showPhone, showTitle }
      if (list) await api.patch(`/api/lists/${list.id}`, body)
      else await api.post('/api/lists', body)
      toast.success(list ? 'Saved.' : `${name.trim()} added.`)
      onSaved()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={list ? `Edit ${list.name}` : 'Add a list'} onClose={onClose} width={460}>
      <form onSubmit={save}>
        {/* The look sits with the name because the two are the same decision — what this
            list is called and what it looks like — and because the tile is where the eye
            lands first in every place a list is drawn. */}
        <div className="field">
          <label>Name</label>
          <div className="list-name-row">
            <LookPicker
              color={color}
              icon={icon}
              onChange={(look) => { setColor(look.color); setIcon(look.icon) }}
            />
            <input
              type="text"
              autoFocus
              maxLength={60}
              placeholder="On call"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <label>Description</label>
          <textarea
            className="notify-note"
            maxLength={280}
            placeholder="What this list is for, for whoever opens it."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {/* What each person's chip carries beyond the two things it always does. Per list,
            because they want different things: an on-call list is for ringing somebody,
            a list of who is at the safety course is not. */}
        <div className="field">
          <label>Show beside each name</label>
          <div className="list-show">
            <div className="list-show-row">
              <span>Phone number</span>
              <Switch checked={showPhone} onChange={setShowPhone} label="Show phone number" />
            </div>
            <div className="list-show-row">
              <span>Title</span>
              <Switch checked={showTitle} onChange={setShowTitle} label="Show title" />
            </div>
          </div>
          <span className="hint">The name and the note are always shown.</span>
        </div>
        <div className="modal-actions">
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="submit" disabled={busy || !name.trim()}>
            {list ? 'Save' : 'Add list'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
